<template>
  <!-- prettier-ignore -->
  <div class="min-h-screen bg-gray-50 bg-white">
    <div class="p-6 bg-white shadow-sm border-b border-slate-200">
      <button
        type="button"
        class="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wide text-primary"
        @click="$router.back()"
      >
      <Icon icon-name="BackIcon" />
        Back to Dashboard
      </button>
        <div class="mt-4">
        <h1 class=" text-base font-bold text-gray-800">Feature Usage Dashboard</h1>
        <p class="mt-1 text-[12px] text-gray-600">Monitor feature adoption and usage across all tenants</p>
      </div>
    </div>
    <div v-if="hasMetrics" :class="['grid gap-6 p-6', metricsGridClass]">
      <div
        v-for="metric in metrics"
        :key="metric.title"
      >
        <MetricCard> 
       <div class="flex justify-between">
           <div>
            <p class="text-[12px] text-grayish">{{ metric.title }}</p>
            <p class="mt-4 text-2xl font-bold text-slate-800">{{ metric.value }}</p>
            <p :class="['mt-3 text-[12px] text-green', metric.subtitleClass]">{{ metric.subtitle }}</p>
          </div>
          <Icon :icon-name="metric.icon" />
       </div>
        </MetricCard>
      </div>
    </div>
    <hr class="height-[1px] bg-Slate/200 m-6 mt-0 shadow-sm " />
    <div v-if="additionalMetrics.length" :class="['grid gap-6 p-6 pt-0', additionalGridClass]">
      <div
        v-for="am in additionalMetrics"
        :key="am.title"
      >
        <MetricCard>
          <div class="flex justify-between">
            <div>
              <p class="text-[12px] text-grayish">{{ am.title }}</p>
              <p class="mt-4 text-2xl font-bold text-slate-800">{{ am.value }}</p>
              <p :class="['mt-3 text-[12px] text-green', am.subtitleClass]">{{ am.subtitle }}</p>
            </div>
            <Icon :icon-name="am.icon" />
          </div>
        </MetricCard>
      </div>
    </div>
    <div class="contactUsage p-6">
      <MetricCard>
        <div class="flex items-center justify-between">
          <div>
            <h2 class="text-base font-bold text-slate-800">Contacts Usage</h2>
            <p class="mt-1 text-[12px] text-grayish">Monthly usage trend across tenants</p>
          </div>
          <!-- <FilterDropdown
            :items="filterItems"
            label="All Tenants"
            iconName="DropdownIcon"
            titleField="label"
            :showLeadingVisual="false"
            :iconReverse="true"
            @select="selectTenant"
          /> -->
        </div>
        <Loader v-if="chartLoading" class="mt-6" />
        <LineChart
          v-else
          class="mt-6"
          :series="chartSeries"
          :categories="chartCategories"
          categoryTitle="Months of the year"
          :height="350"
        />
      </MetricCard>
    </div>
    <!-- Bar chart: only rendered for bi_dashboard feature -->
    <div v-if="selectedFeature === 'bi_dashboard'" class="biDashboardBarChart p-6 pt-0">
      <MetricCard>
        <div class="flex items-center justify-between mb-6">
          <div>
            <h2 class="text-base font-bold text-slate-800">Dashboards by Template</h2>
            <p class="mt-1 text-[12px] text-grayish">Number of dashboards created per template</p>
          </div>
        </div>
        <Loader v-if="chartLoading" class="mt-6" />
        <BarChart
          v-else
          :series="barChartSeries"
          :categories="barChartCategories"
          yAxisTitle="Dashboards Created"
          :height="350"
        />
      </MetricCard>
    </div>
    <div class="featureTable p-6">
      <MetricCard class="flex flex-col gap-6">
        <div class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <SearchBar @update="handleSearch" />
          <div class="flex flex-wrap items-center gap-3 justify-end">
            <DateFilter
              :value="selectedDatePreset"
              class-name="border border-slate-200"
              @change="handleDateChange"
            />
             <FilterDropdown
                    :items="[{id: 'all', title: 'All'}, {id: 1, title: 'Starter'}, { id: 2, title: 'Standard'}, {id: 3, title: 'Professional'}, {id: 4, title: 'Premium'}]"
                    label="Plan"
                    iconName="StarIcon"
                    @select="selectPlan"
                />
            <button type="button" class="px-5 py-4 border border-blue-600 rounded-md text-blue-600 flex items-center gap-3 font-bold h-fit outline-none focus:outline-none " :disabled="exportLoading" @click="exportCsv"
            >
                    <Icon icon-name="DownloadIcon" />
                    <div>
                        Export CSV
                    </div>
                </button>
          </div>
        </div>
        <Table
          :columns="tenantColumns"
          :data="filteredTenants"
          :loading="tableLoading"
          sort-mode="internal"
        />
        <div v-if="pagination && pagination.total_pages > 1" class="flex justify-end">
          <RamroPagination
            :current="pagination.current_page"
            :total="pagination.total_pages"
            @update="handlePageChange"
          />
        </div>
      </MetricCard>
    </div>
  </div>
</template>
<script>
import Icon from "../components/Icon.vue";
import MetricCard from "../components/MetricCard.vue";
import Loader from "../components/Loader.vue";
import LineChart from "../components/charts/LineChart.vue";
import BarChart from "../components/charts/BarChart.vue";
import SearchBar from "../components/SearchBar.vue";
import Table from "../components/Table.vue";
import DateFilter from "../components/DateFilter.vue";
import FilterDropdown from "../components/FilterDropdown.vue";
import RamroPagination from "@/components/Pagination/Pagination.vue";
import iconUtils from "../utils/iconUtils.js";
import axios from "axios";
import { getFeatureAnalytics, downloadFeatureAnalyticsCsv } from "../apis/calls.js";

export default {
  name: "IndividualFeatureUsage",
  components: {
    Icon,
    MetricCard,
    FilterDropdown,
    Loader,
    LineChart,
    BarChart,
    SearchBar,
    Table,
    DateFilter,
    RamroPagination,
  },
  data() {
    return {
      metrics: [
        {
          title: "Tenants using Feature",
          value: "0",
          subtitle: "-",
          subtitleClass: "text-emerald-500",
          icon: "AdoptionIcon"
        },
        {
          title: "Total Usage",
          value: "0",
          subtitle: "-",
          subtitleClass: "text-emerald-500",
          icon: "TotalUsageIcon"
        },
        {
          title: "Average Monthly Usage",
          value: "0",
          subtitle: "-",
          subtitleClass: "text-slate-400",
          icon: "MonthlyUsageIcon"
        }
      ],
      chartLoading: false,
      filterItems: [],
      additionalMetrics: [],
      chartCategories: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec"
      ],
      baseSeries: {
        all: [7200, 5400, 5000, 7600, 6100, 4800, 9200, 7800, 7200, 4500, 6800, 7100],
        contacts: [6500, 5200, 4900, 7200, 5900, 4600, 8700, 7600, 6900, 4300, 6400, 6800],
        agents: [5200, 4100, 4300, 5800, 4700, 4200, 6300, 6100, 5600, 3900, 5200, 5400]
      },
      chartSeries: [],
      // bi_dashboard bar chart data
      barChartSeries: [],
      barChartCategories: [],
      exportLoading: false,
      tableLoading: false,
      selectedDatePreset: "this_month",
      selectedDateRange: { from: "", to: "" },
      selectedPlan: "all",
      selectedTenant: null,
      selectedFeature: "contacts",
      searchQuery: "",
      tenantUsageRows: [],
      pagination: {
        current_page: 1,
        per_page: 10,
        total: 0,
        total_pages: 1,
        from: 0,
        to: 0
      },
      currentPage: 1,
      perPage: 10
    };
  },
  computed: {
    hasMetrics() {
      return this.metrics.length > 0;
    },
    metricsGridClass() {
      return this.metrics.length >= 4 ? 'md:grid-cols-4' : 'md:grid-cols-3';
    },
    additionalGridClass() {
      const n = this.additionalMetrics.length;
      return n <= 3 ? 'md:grid-cols-3' : 'md:grid-cols-4';
    },
    tenantColumns() {
      return [
        { key: "tenant_name", header: "Tenant Name", sortable: true, render: this.getTenantNameRender },
        { key: "plan", header: "Plan", sortable: true, render: this.getPlanRender },
        { key: "branch_count", header: "Branch Count", sortable: true },
        { key: "user_count", header: "User Count", sortable: true },
        { key: "total_usage", header: "Total Usage", sortable: true, render: ({ row }) => this.formatNumber(row.total_usage) },
        { key: "average_monthly_usage", header: "Average Monthly Usage", sortable: true, render: ({ row }) => this.formatNumber(row.average_monthly_usage) },
        { key: "users_using_feature", header: "Users Using Feature", sortable: true, render: ({ row }) => this.formatNumber(row.users_using_feature) },
        { key: "last_used_date", header: "Last Used Date", sortable: true, render: ({ row }) => this.formatLastUsedDate(row.last_used_date) }
      ];
    },
    filteredTenants() {
      return this.tenantUsageRows
        .filter((row) => {
          if (this.selectedPlan === "all") return true;
          const planStr = (row && row.plan ? String(row.plan) : '').toLowerCase();
          const sel = String(this.selectedPlan || '').toLowerCase();
          return sel ? planStr.includes(sel) : true;
        })
        .filter((row) => {
          if (!this.searchQuery) return true;
          return row.tenant_name.toLowerCase().includes(this.searchQuery.toLowerCase());
        })
        .filter((row) => {
          if (!this.selectedTenant) return true;
          const tenantQuery = String(this.selectedTenant).toLowerCase();
          const nameMatches = (row.tenant_name || '').toLowerCase().includes(tenantQuery);
          const businessMatches = (row.business_name || '').toLowerCase().includes(tenantQuery);
          const subdomainMatches = (row.subdomain || '').toLowerCase().includes(tenantQuery);
          return nameMatches || businessMatches || subdomainMatches;
        });
    }
  },
  created() {
    this.initializeDateRange();
    if (this.$route && this.$route.params && this.$route.params.feature) {
      this.selectedFeature = this.$route.params.feature;
    }
  },
  mounted() {
    this.fetchFeatureData();
    this.fetchTenants();
  },
  methods: {
    selectTenant(option) {
      this.selectedTenant = option && (option.id || option.title) ? (option.id || option.title) : null;
    },
    async fetchTenants(page = 1, search = '') {
      try {
        const resp = await axios.get(`tenants?include=subscription&page[number]=${page}&search=${encodeURIComponent(search)}`);
        const data = resp && resp.data ? resp.data : [];
        const items = Array.isArray(data) ? data : (Array.isArray(data.data) ? data.data : []);
        this.filterItems = items.map(t => ({ id: t.business_name || t.subdomain || String(t.id), label: t.business_name || t.subdomain || `Tenant ${t.id}` }));
      } catch (e) {
        this.filterItems = [];
      }
    },
    getSeriesByFilter(key) {
      const data = this.baseSeries[key] || this.baseSeries.all;
      return [
        {
          name: "Contacts Usage",
          data
        }
      ];
    },
    handleSearch(value) {
      this.searchQuery = value;
      this.currentPage = 1;
      this.fetchFeatureData(1);
    },
    handleDateChange(payload) {
      if (!payload) {
        return;
      }
      this.selectedDatePreset = payload.preset || this.selectedDatePreset;
      this.selectedDateRange = {
        from: payload.from || "",
        to: payload.to || ""
      };
      this.currentPage = 1;
      this.fetchFeatureData(1);
    },
    getTodayDate() {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day = String(today.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    getDateQueryParams() {
      const preset = this.selectedDatePreset;
      const range = this.selectedDateRange || {};
      const fallbackDate = range.to || range.from || this.getTodayDate();

      switch (preset) {
        case "today":
          return { date_operator: "is_today", date: fallbackDate };
        case "yesterday":
          return { date_operator: "is_yesterday", date: fallbackDate };
        case "this_week":
          return { date_operator: "since_7_days", date: fallbackDate };
        case "this_month":
          return { date_operator: "since_last_30_days", date: fallbackDate };
        case "custom":
          if (range.from && range.to) {
            return {
              date_operator: "is_custom",
              date: JSON.stringify({ from: range.from, to: range.to })
            };
          }
          return {};
        default:
          return {};
      }
    },
    selectPlan(value) {
      if (!value) return;
      if (value.id === 'all' || value.title === 'All') {
        this.selectedPlan = 'all';
      } else {
        this.selectedPlan = value.title || value;
      }
      this.currentPage = 1;
      this.fetchFeatureData(1);
    },
    async exportCsv() {
      if (this.exportLoading) return;
      this.exportLoading = true;
      try {
        const dateParams = this.getDateQueryParams();
        const resp = await downloadFeatureAnalyticsCsv({
          feature: this.selectedFeature,
          plan: this.selectedPlan === 'all' ? undefined : this.selectedPlan,
          search: this.searchQuery || undefined,
          date_operator: dateParams.date_operator,
          date: dateParams.date
        });
        const url = window.URL.createObjectURL(new Blob([resp.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${this.selectedFeature}-usage.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        this.$toasted?.success('Feature usage CSV export started');
      } catch (e) {
        alert('Error exporting CSV');
      } finally {
        this.exportLoading = false;
      }
    },
    formatNumber(value) {
      if (value == null) {
        return "-";
      }
      return Number(value).toLocaleString();
    },
    formatLastUsedDate(date) {
      if (!date) {
        return "-";
      }
      if (typeof date === "string" && date.includes(" ")) {
        return date.split(" ")[0];
      }
      return date;
    },
    getTenantNameRender({ row }) {
      const imageHtml = row.profile_image 
        ? `<img src="${row.profile_image}" class="w-10 h-10 rounded-full" alt="${row.tenant_name}" />`
        : `<div class="w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold">${row.tenant_name.charAt(0).toUpperCase()}</div>`;
      return `<div class="flex items-center gap-2">
        ${imageHtml}
        <span>${row.tenant_name}</span>
      </div>`;
    },
    getPlanColor(plan) {
      if (!plan || typeof plan !== "string") {
        return "gray";
      }
      const normalized = plan.toLowerCase();
      if (normalized.includes("starter")) return "yellow";
      if (normalized.includes("standard")) return "gray";
      if (normalized.includes("professional")) return "blue";
      if (normalized.includes("premium")) return "green";
      return "gray";
    },
    getPlanRender({ row }) {
      const plan = row.plan || "Unknown";
      const normalized = plan.toLowerCase();
      let bgColor = "#F3F4F6"; // default gray
      let textColor = "#1F2937";
      if (normalized.includes("starter")) {
        bgColor = "#FEF3C7";
        textColor = "#92400E";
      } else if (normalized.includes("standard")) {
        bgColor = "#F3F4F6";
        textColor = "#1F2937";
      } else if (normalized.includes("professional")) {
        bgColor = "#E6F6FE";
        textColor = "#0369A1";
      } else if (normalized.includes("premium")) {
        bgColor = "#D1FAE5";
        textColor = "#065F46";
      }
      const displayLabel = normalized.includes('starter') ? 'Starter'
        : normalized.includes('standard') ? 'Standard'
        : normalized.includes('professional') ? 'Professional'
        : normalized.includes('premium') ? 'Premium'
        : plan;
      return `<div class="px-3 py-1 text-sm font-semibold rounded-full flex items-center justify-center" style="background-color: ${bgColor}; color: ${textColor}; border-radius: 20px;">${displayLabel}</div>`;
    },
    getThisMonthUsageRender({ row }) {
      const usage = row.this_month_usage;
      if (!usage) {
        return "-";
      }
      const value = this.formatNumber(usage.value);
      const isUp = usage.direction === "up";
      const badgeClass = isUp ? "" : "text-red-600";
      const badgeStyle = isUp ? "color: #065F46;" : "";
      const indicator = isUp ? iconUtils.UpIndicator : iconUtils.DownIndicator;
      return `<div class="flex flex-col gap-1">
        <span class="text-sm font-semibold text-slate-800">${value}</span>
        <span class="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded ${badgeClass}" style="${badgeStyle}">
          <span>${indicator}</span>
          <span>${usage.change}</span>
        </span>
      </div>`;
    },
    getPlanButtonClasses(value) {
      return [
        "px-4 py-2 rounded-md border text-sm font-semibold transition-colors",
        this.selectedPlan === value
          ? "border-blue-500 text-blue-600 bg-blue-50"
          : "border-slate-200 text-slate-600 bg-white"
      ].join(" ");
    },
    initializeDateRange() {
      const today = new Date();
      const format = (date) => date.toISOString().split("T")[0];
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      this.selectedDateRange = {
        from: format(start),
        to: format(today)
      };
    },
    async fetchFeatureData(page = this.currentPage) {
      this.tableLoading = true;
      this.chartLoading = true;
      try {
        this.currentPage = page;
        const dateParams = this.getDateQueryParams();
        const params = {
          feature: this.selectedFeature,
          plan: this.selectedPlan === 'all' ? undefined : this.selectedPlan,
          search: this.searchQuery || undefined,
          page: this.currentPage,
          per_page: this.perPage,
          date_operator: dateParams.date_operator,
          date: dateParams.date
        };
        const resp = await getFeatureAnalytics(params);
        const data = resp && resp.data ? resp.data : {};

        // Map metrics from summary
        const s = data.summary || {};
        this.metrics = [
          {
            title: "Tenants using Feature",
            value: this.formatNumber(s.tenants_using_feature || 0),
            subtitle: s.adoption_rate != null ? `${Number(s.adoption_rate).toFixed(2)}% Adoption Rate` : "",
            subtitleClass: "text-emerald-500",
            icon: "AdoptionIcon"
          },
          {
            title: "Total Usage",
            value: this.formatNumber(s.total_usage || 0),
            subtitle: s.growth_percentage != null && s.growth_direction ? `${s.growth_direction === 'down' ? '-' : s.growth_direction === 'up' ? '+' : ''}${Number(s.growth_percentage).toFixed(2)}% from last period` : "",
            subtitleClass: (s.growth_direction === 'down') ? "text-red-500" : (s.growth_direction === 'up') ? "text-emerald-500" : "text-slate-400",
            icon: "TotalUsageIcon"
          },
          {
            title: "Average Monthly Usage",
            value: (s.average_monthly_usage != null) ? Number(s.average_monthly_usage).toFixed(2) : "0.00",
            subtitle: "Per Tenant",
            subtitleClass: "text-slate-400",
            icon: "MonthlyUsageIcon"
          }
        ];

        // bi_dashboard: 4th KPI card + bar chart
        if (this.selectedFeature === 'bi_dashboard') {
          this.metrics.push({
            title: 'Contact Usage',
            value: this.formatNumber(s.contact_usage ?? 0),
            subtitle: 'Widgets built on contact data',
            subtitleClass: 'text-slate-400',
            icon: 'TotalApplicationAdded'
          });

          const templateBreakdown = data.chart_data && Array.isArray(data.chart_data.template_breakdown)
            ? data.chart_data.template_breakdown
            : [];
          this.barChartCategories = templateBreakdown.map(t => t.template);
          this.barChartSeries = [{ name: 'Dashboards Created', data: templateBreakdown.map(t => t.count) }];
        }

        // Map chart data
        const chartData = data.chart_data && Array.isArray(data.chart_data.monthly_usage) ? data.chart_data.monthly_usage : [];
        this.chartCategories = chartData.map(item => item.month);
        this.chartSeries = [{ name: 'Usage', data: chartData.map(item => item.value || 0) }];

        // Map tenants table rows
        this.tenantUsageRows = Array.isArray(data.tenants) ? data.tenants : [];
        const pagination = data.pagination || {};
        this.pagination = {
          current_page: pagination.current_page || this.currentPage || 1,
          per_page: pagination.per_page || this.perPage,
          total: pagination.total || (Array.isArray(data.tenants) ? data.tenants.length : 0),
          total_pages: pagination.total_pages || 1,
          from: pagination.from || 0,
          to: pagination.to || 0
        };
        this.currentPage = this.pagination.current_page;
        this.perPage = this.pagination.per_page;
        // Map additional metrics from API: [{ key, label, value }]
        const am = Array.isArray(data.additional_metrics) ? data.additional_metrics : [];
        const metricIcons = [
          'TotalApplicationAdded',
          'TotalInterestedServiceAdded',
          'TotalServiceCompared'
        ];
        this.additionalMetrics = am.map((m, index) => ({
          key: m.key,
          title: m.label || '',
          value: m.value != null ? this.formatNumber(m.value) : '0',
          subtitle: '',
          subtitleClass: 'text-slate-400',
          icon: metricIcons[index % metricIcons.length]
        }));
      } catch (e) {
        this.tenantUsageRows = [];
        this.metrics = [];
        this.chartCategories = [];
        this.chartSeries = [];
        this.barChartSeries = [];
        this.barChartCategories = [];
        this.additionalMetrics = [];
        this.pagination = {
          current_page: 1,
          per_page: this.perPage,
          total: 0,
          total_pages: 1,
          from: 0,
          to: 0
        };
        this.currentPage = 1;
      } finally {
        this.tableLoading = false;
        this.chartLoading = false;
      }
    },
    handlePageChange({ page }) {
      if (!page || page === this.currentPage) {
        return;
      }
      this.fetchFeatureData(page);
    }
  }
};
</script>
<style>
#content-area {
    padding-left: 0px !important;
    padding-right: 0px !important;
}
</style>

